import React, { useState } from 'react';
import {
  BookOpen,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  AlertCircle,
  Upload,
  Award,
  Users,
  TrendingUp,
  DollarSign,
  Star,
  Globe,
  Shield,
  Zap
} from 'lucide-react';
import { Helmet } from 'react-helmet';

interface MentorAuthProps {
  onLogin: () => void;
}

const MentorAuth: React.FC<MentorAuthProps> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    bio: '', expertise: '', experience: '', linkedin: '', website: '', agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (!isLogin) {
      if (!formData.name) newErrors.name = 'Name is required';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      if (!formData.bio) newErrors.bio = 'Bio is required';
      if (!formData.expertise) newErrors.expertise = 'Expertise is required';
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setTimeout(() => { onLogin(); }, 1000);
    }
  };

  const features = [
    { icon: <Upload className="w-5 h-5" />, title: 'Easy Course Creation', description: 'Upload PDFs and automatically convert them to interactive courses' },
    { icon: <Users className="w-5 h-5" />, title: 'Global Reach', description: 'Reach thousands of students worldwide with your expertise' },
    { icon: <DollarSign className="w-5 h-5" />, title: 'Earn Revenue', description: 'Set your own prices and earn up to 70% revenue share' },
    { icon: <TrendingUp className="w-5 h-5" />, title: 'Analytics Dashboard', description: 'Track your course performance with detailed analytics' },
    { icon: <Shield className="w-5 h-5" />, title: 'Secure Platform', description: 'Your content is protected with enterprise-grade security' },
    { icon: <Award className="w-5 h-5" />, title: 'Mentor Verification', description: 'Get verified mentor badge to build trust with students' }
  ];

  const stats = [
    { label: 'Active Mentors', value: '2,500+' },
    { label: 'Students Taught', value: '150K+' },
    { label: 'Courses Created', value: '12K+' },
    { label: 'Revenue Generated', value: '₹50Cr+' }
  ];

  const inputClass = (field: string) =>
    `w-full pl-10 pr-4 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 text-slate-900 placeholder-slate-400 ${
      errors[field] ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-navy-300 focus:border-navy-400'
    }`;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Left Side */}
        <div className="hidden lg:flex lg:w-1/2 bg-navy-950 p-12 flex-col justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-12">
              <div className="bg-navy-500 p-3 rounded-xl">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white font-serif">Mentor Portal</h1>
                <p className="text-white/60 text-sm">Share your expertise, earn revenue</p>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-serif leading-tight">
                Transform Your Knowledge Into Income
              </h2>
              <p className="text-white/70 mb-8 leading-relaxed">
                Join thousands of successful mentors who are earning by teaching what they love.
              </p>

              <div className="grid grid-cols-1 gap-5">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-white/10 p-2.5 rounded-lg text-navy-300 flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-0.5 text-sm">{feature.title}</h3>
                      <p className="text-white/60 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white/5 rounded-xl p-4">
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-white/50 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="bg-navy-500 p-3 rounded-xl">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 font-serif">Mentor Portal</h1>
              </div>
              <p className="text-slate-500 text-sm">Share your expertise, earn revenue</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2 font-serif">
                  {isLogin ? 'Welcome Back' : 'Become a Mentor'}
                </h2>
                <p className="text-slate-500 text-sm">
                  {isLogin ? 'Sign in to your mentor account' : 'Join our community of expert mentors'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className={inputClass('name')} placeholder="Enter your full name" />
                    </div>
                    {errors.name && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.name}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={inputClass('email')} placeholder="Enter your email" />
                  </div>
                  {errors.email && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className={`w-full pl-10 pr-12 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 text-slate-900 placeholder-slate-400 ${errors.password ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-navy-300 focus:border-navy-400'}`}
                      placeholder="Enter your password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.password}</p>}
                </div>

                {!isLogin && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password *</label>
                      <div className="relative">
                        <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          className={inputClass('confirmPassword')} placeholder="Confirm your password" />
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.confirmPassword}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Professional Bio *</label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        rows={3}
                        className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 text-slate-900 placeholder-slate-400 resize-none ${errors.bio ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-navy-300 focus:border-navy-400'}`}
                        placeholder="Tell us about your professional background and expertise..."
                      />
                      {errors.bio && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.bio}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Areas of Expertise *</label>
                      <input type="text" value={formData.expertise} onChange={(e) => setFormData({...formData, expertise: e.target.value})}
                        className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 text-slate-900 placeholder-slate-400 ${errors.expertise ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-navy-300 focus:border-navy-400'}`}
                        placeholder="e.g., AI Trading, Technical Analysis, Risk Management" />
                      {errors.expertise && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.expertise}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">LinkedIn Profile</label>
                        <input type="url" value={formData.linkedin} onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 placeholder-slate-400"
                          placeholder="https://linkedin.com/in/..." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Website</label>
                        <input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 placeholder-slate-400"
                          placeholder="https://yourwebsite.com" />
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox" id="agreeToTerms" checked={formData.agreeToTerms}
                        onChange={(e) => setFormData({...formData, agreeToTerms: e.target.checked})}
                        className="mt-1 w-4 h-4 text-navy-500 bg-white border-slate-300 rounded focus:ring-navy-400"
                      />
                      <label htmlFor="agreeToTerms" className="text-sm text-slate-600">
                        I agree to the{' '}
                        <a href="/terms" className="text-navy-500 hover:text-navy-700 underline underline-offset-1" aria-label="Terms of Service">Terms of Service</a>{' '}
                        and{' '}
                        <a href="/privacy" className="text-navy-500 hover:text-navy-700 underline underline-offset-1" aria-label="Privacy Policy">Privacy Policy</a>
                      </label>
                    </div>
                    {errors.agreeToTerms && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.agreeToTerms}</p>}
                  </>
                )}

                <button
                  type="submit"
                  className="w-full bg-navy-500 hover:bg-navy-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                >
                  <span>{isLogin ? 'Sign In' : 'Create Mentor Account'}</span>
                  <Zap className="w-4 h-4" />
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm">
                  {isLogin ? "Don't have an account?" : 'Already have an account?'}
                  <button
                    onClick={() => { setIsLogin(!isLogin); setErrors({}); setFormData({ name: '', email: '', password: '', confirmPassword: '', bio: '', expertise: '', experience: '', linkedin: '', website: '', agreeToTerms: false }); }}
                    className="ml-2 text-navy-500 hover:text-navy-700 font-medium transition-colors"
                  >
                    {isLogin ? 'Become a Mentor' : 'Sign In'}
                  </button>
                </p>
              </div>

              {isLogin && (
                <div className="mt-4 text-center">
                  <a href="#" className="text-navy-500 hover:text-navy-700 text-sm transition-colors">Forgot your password?</a>
                </div>
              )}
            </div>

            {/* Trust indicators */}
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center flex-wrap gap-6 text-slate-500">
                <div className="flex items-center space-x-1.5">
                  <Star className="w-4 h-4 text-amber-400 fill-current" />
                  <span className="text-sm">4.9/5 Mentor Rating</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <Users className="w-4 h-4 text-navy-500" />
                  <span className="text-sm">2,500+ Mentors</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <Globe className="w-4 h-4 text-navy-500" />
                  <span className="text-sm">Global Reach</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MentorAuth;
